import {
  isWebSocketConnectionMode,
  SimulatorType,
  WebSocketConnectionMode,
} from "@/types/api/SimulatorType"
import type { FormData } from "@/types/FormData"
import { asJsonObjectValueOrNull } from "@/types/json"
import { isProductionDeployment } from "@/utils/auth/dev-access"
import { isHttpMethod } from "@/utils/simulation/httpMethod"
import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

import { validateJsonTemplatePlaceholder } from "./jsonTemplateValidation"
import { parseStrictInteger } from "./realtimeAgentValidation"

const VALID_WEBSOCKET_COMPAT_PROFILES = new Set([
  "generic",
  "json_audio",
  "legacy_checkmate_raw_pcm",
])
const VALID_SAMPLE_RATES = [8000, 16000, 24000, 48000]
const AUDIO_DATA_PLACEHOLDER = "{{audio_data}}"

export function hasValidHostname(url: string): boolean {
  try {
    const parsed = new URL(url)
    return parsed.hostname.length > 0
  } catch {
    return false
  }
}

function hasAllowedWebsocketScheme(url: string): boolean {
  return (
    url.startsWith("wss://") ||
    (url.startsWith("ws://") && !isProductionDeployment())
  )
}

export function validateJsonObjectField(
  value: unknown,
  fieldLabel: string,
): string | null {
  if (
    typeof value !== "string" ||
    value.trim() === "" ||
    value.trim() === "{}"
  ) {
    return null
  }
  try {
    const parsed: unknown = JSON.parse(value)
    if (!asJsonObjectValueOrNull(parsed)) {
      return `${fieldLabel} must be a JSON object (e.g. {"key": "value"}).`
    }
  } catch {
    return `${fieldLabel} must be valid JSON (e.g. {"key": "value"}).`
  }
  return null
}

function validateHttpFirstFields(
  metadata: Record<string, unknown>,
  agentTypeLabel: string,
): string | null {
  const httpUrl = metadata.http_url
  if (typeof httpUrl !== "string" || httpUrl.trim() === "") {
    return `HTTP Endpoint URL is required for ${agentTypeLabel} agents with HTTP-first connection mode.`
  }
  if (
    !httpUrl.trim().startsWith("https://") &&
    !httpUrl.trim().startsWith("http://")
  ) {
    return "HTTP Endpoint URL must start with https:// or http://."
  }
  if (!hasValidHostname(httpUrl.trim())) {
    return "HTTP Endpoint URL must include a valid hostname."
  }

  const responsePath = metadata.websocket_url_response_path
  if (typeof responsePath !== "string" || responsePath.trim() === "") {
    return `WebSocket URL Path in Response is required for ${agentTypeLabel} agents with HTTP-first connection mode.`
  }

  const httpMethod = metadata.http_method
  if (typeof httpMethod === "string" && !isHttpMethod(httpMethod)) {
    return "HTTP Method must be one of: DELETE, GET, HEAD, OPTIONS, PATCH, POST, PUT."
  }

  return (
    validateJsonObjectField(metadata.http_request_body, "HTTP Request Body") ??
    validateJsonObjectField(metadata.http_headers, "HTTP Headers")
  )
}

function validateTwimlWebhookFields(
  metadata: Record<string, unknown>,
): string | null {
  const voiceUrl = metadata.voice_url
  if (typeof voiceUrl !== "string" || voiceUrl.trim() === "") {
    return "Voice Webhook URL is required for TwiML Voice Webhook mode."
  }
  if (
    !voiceUrl.trim().startsWith("https://") &&
    !voiceUrl.trim().startsWith("http://")
  ) {
    return "Voice Webhook URL must start with https:// or http://."
  }
  if (!hasValidHostname(voiceUrl.trim())) {
    return "Voice Webhook URL must include a valid hostname."
  }

  const httpMethod = metadata.voice_http_method
  if (
    httpMethod !== undefined &&
    (typeof httpMethod !== "string" ||
      !["GET", "POST"].includes(httpMethod.trim().toUpperCase()))
  ) {
    return "Voice HTTP Method must be GET or POST."
  }

  const headersError = validateJsonObjectField(
    metadata.voice_http_headers,
    "Voice Webhook Headers",
  )
  if (headersError) {
    return headersError
  }
  const formFieldsError = validateJsonObjectField(
    metadata.voice_form_fields,
    "Additional Form Fields",
  )
  if (formFieldsError) {
    return formFieldsError
  }

  if (metadata.audio_encoding != null && metadata.audio_encoding !== "ulaw") {
    return "TwiML Voice Webhook mode requires G.711 mu-law audio encoding."
  }

  for (const [fieldName, fieldLabel] of [
    ["send_sample_rate_hertz", "Send"],
    ["send_sample_rate_hz", "Send"],
    ["receive_sample_rate_hertz", "Receive"],
    ["receive_sample_rate_hz", "Receive"],
  ] as const) {
    const rate = metadata[fieldName]
    if (rate != null && parseStrictInteger(rate) !== 8000) {
      return `${fieldLabel} sample rate must be 8,000 Hz in TwiML Voice Webhook mode.`
    }
  }

  return null
}

function validateJsonTemplate(
  template: unknown,
  placeholderName: string,
  fieldLabel: string,
): string | null {
  if (typeof template !== "string" || template.trim() === "") {
    return null
  }
  if (!template.includes(`{{${placeholderName}}}`)) {
    return `${fieldLabel} must contain the {{${placeholderName}}} placeholder.`
  }
  try {
    const withPlaceholders = template
      .replace(/"\{\{[^}]+\}\}"/g, '"PLACEHOLDER"')
      .replace(/\{\{[^}]+\}\}/g, "0")
    JSON.parse(withPlaceholders)
  } catch {
    return `${fieldLabel} must be a valid JSON template.`
  }
  return null
}

function validateAudioTemplate(template: unknown): string | null {
  if (typeof template !== "string" || template.trim() === "") {
    return null
  }

  const validationError = validateJsonTemplatePlaceholder(
    template,
    AUDIO_DATA_PLACEHOLDER,
    { allowBarePlaceholder: true, requireObject: true },
  )
  if (validationError === "invalid_json") {
    return "Send audio template must be a valid JSON template."
  }
  if (validationError === "invalid_root") {
    return "Send audio template must be a JSON object or the bare {{audio_data}} placeholder."
  }
  if (validationError !== null) {
    return "Send audio template must contain exactly one {{audio_data}} occurrence total, and it must be inside a JSON value."
  }
  return null
}

function validateMediaTemplate(template: unknown): string | null {
  if (typeof template !== "string" || template.trim() === "") {
    return null
  }
  if (!template.includes("{{media_data}}")) {
    return "Media send template must contain the {{media_data}} placeholder."
  }
  if (template.trim() === "{{media_data}}") {
    return null
  }
  try {
    const withPlaceholders = template
      .replace(/"\{\{[^}]+\}\}"/g, '"PLACEHOLDER"')
      .replace(/\{\{[^}]+\}\}/g, "0")
    JSON.parse(withPlaceholders)
  } catch {
    return "Media send template must be a valid JSON template."
  }
  return null
}

function validateDirectEndpoint(
  endpoint: unknown,
  required: boolean,
): string | null {
  if (typeof endpoint !== "string" || endpoint.trim() === "") {
    return required
      ? "WebSocket Endpoint is required for Chat WebSocket agents in direct mode."
      : null
  }
  const trimmed = endpoint.trim()
  if (!hasAllowedWebsocketScheme(trimmed)) {
    return "WebSocket Endpoint must be a secure URL starting with wss:// (e.g. wss://example.com/socket)."
  }
  if (!hasValidHostname(trimmed)) {
    return "WebSocket Endpoint must include a valid hostname."
  }
  return null
}

function validateSharedFields(
  metadata: Record<string, unknown>,
  validateInitializationJson: boolean,
): string | null {
  if (validateInitializationJson) {
    const initializationError = validateJsonObjectField(
      metadata.initialization_json,
      "Initialization JSON",
    )
    if (initializationError) {
      return initializationError
    }
  }
  return validateJsonObjectField(metadata.custom_headers, "Custom Headers")
}

function validateVoiceWebsocketFields(
  metadata: Record<string, unknown>,
): string | null {
  const sendRate = metadata.send_sample_rate_hertz
  if (
    sendRate != null &&
    sendRate !== "" &&
    !VALID_SAMPLE_RATES.includes(parseStrictInteger(sendRate) as number)
  ) {
    return "Send sample rate must be one of: 8,000, 16,000, 24,000, or 48,000 Hz."
  }

  const receiveRate = metadata.receive_sample_rate_hertz
  if (
    receiveRate != null &&
    receiveRate !== "" &&
    !VALID_SAMPLE_RATES.includes(parseStrictInteger(receiveRate) as number)
  ) {
    return "Receive sample rate must be one of: 8,000, 16,000, 24,000, or 48,000 Hz."
  }

  const receiveChannels = metadata.receive_audio_channels
  if (receiveChannels !== undefined && receiveChannels !== "") {
    const channelValue = parseStrictInteger(receiveChannels)
    if (channelValue !== 1 && channelValue !== 2) {
      return "Receive audio channels must be either 1 or 2."
    }
  }

  const eventMessageTypes = metadata.non_audio_event_message_types
  if (eventMessageTypes !== undefined && eventMessageTypes !== null) {
    if (!Array.isArray(eventMessageTypes)) {
      return 'Non-audio event message types must be a list of strings (e.g. ["system_notify"]).'
    }
    if (
      eventMessageTypes.some(
        (messageType) =>
          typeof messageType !== "string" || messageType.trim() === "",
      )
    ) {
      return "Non-audio event message types must each be a non-empty string."
    }
  }

  const compatibilityProfile = metadata.websocket_compat_profile
  if (compatibilityProfile !== undefined && compatibilityProfile !== null) {
    if (typeof compatibilityProfile !== "string") {
      return "WebSocket compatibility profile must be a string."
    }
    const normalized = compatibilityProfile.trim().toLowerCase()
    if (normalized !== "" && !VALID_WEBSOCKET_COMPAT_PROFILES.has(normalized)) {
      return "WebSocket compatibility profile must be generic, json_audio, or legacy_checkmate_raw_pcm."
    }
  }

  const handshakeTimeout = metadata.handshake_timeout_seconds
  if (handshakeTimeout !== undefined && handshakeTimeout !== "") {
    const timeoutValue = parseStrictInteger(handshakeTimeout)
    if (timeoutValue === null || timeoutValue <= 0) {
      return "Handshake timeout must be a positive integer."
    }
  }

  return (
    validateAudioTemplate(metadata.send_audio_template) ??
    validateMediaTemplate(metadata.send_media_template) ??
    validateJsonTemplate(
      metadata.send_dtmf_template,
      "digit",
      "Send DTMF template",
    )
  )
}

function validateChatWebsocketFields(
  metadata: Record<string, unknown>,
): string | null {
  const sendTemplateError = validateJsonTemplate(
    metadata.send_message_template,
    "message",
    "Send message template",
  )
  if (sendTemplateError) {
    return sendTemplateError
  }

  const handshakeTimeout = metadata.handshake_timeout_seconds
  if (handshakeTimeout !== undefined && handshakeTimeout !== "") {
    const timeoutValue = parseStrictInteger(handshakeTimeout)
    if (timeoutValue === null || timeoutValue <= 0) {
      return "Handshake timeout must be a positive integer."
    }
  }

  const coalesceTimeout = metadata.message_coalesce_timeout_seconds
  if (coalesceTimeout !== undefined && coalesceTimeout !== "") {
    const coalesceValue =
      typeof coalesceTimeout === "number"
        ? coalesceTimeout
        : parseFloat(stringifyUnknownValue(coalesceTimeout))
    if (isNaN(coalesceValue) || !isFinite(coalesceValue) || coalesceValue < 0) {
      return "Message coalesce timeout must be >= 0."
    }
  }
  return null
}

export function validateWebsocketAgent(
  simulatorType: SimulatorType | null,
  formMetadata: FormData["metadata"] | undefined,
): string | null {
  const isVoiceWebsocket = simulatorType === SimulatorType.WEBSOCKET
  const isChatWebsocket = simulatorType === SimulatorType.CHAT_WEBSOCKET
  if ((!isVoiceWebsocket && !isChatWebsocket) || !formMetadata) {
    return null
  }

  const metadata: Record<string, unknown> = formMetadata
  const connectionMode = isWebSocketConnectionMode(metadata.connection_mode)
    ? metadata.connection_mode
    : WebSocketConnectionMode.DIRECT
  let connectionError: string | null
  if (connectionMode === WebSocketConnectionMode.HTTP_FIRST) {
    connectionError = validateHttpFirstFields(
      metadata,
      isVoiceWebsocket ? "HTTP Setup + WebSocket" : "Chat WebSocket",
    )
  } else if (connectionMode === WebSocketConnectionMode.TWIML_WEBHOOK) {
    connectionError = isVoiceWebsocket
      ? validateTwimlWebhookFields(metadata)
      : "TwiML Voice Webhook mode is only supported for voice WebSocket agents."
  } else {
    connectionError = validateDirectEndpoint(metadata.endpoint, isChatWebsocket)
  }
  if (connectionError) {
    return connectionError
  }

  const sharedError = validateSharedFields(
    metadata,
    connectionMode !== WebSocketConnectionMode.TWIML_WEBHOOK,
  )
  if (sharedError) {
    return sharedError
  }

  return isVoiceWebsocket
    ? validateVoiceWebsocketFields(metadata)
    : validateChatWebsocketFields(metadata)
}
